// src/shared/ui/SurfaceCard.jsx
//
// The plain "white, bordered, rounded-xl, padded" shell that golive's
// Cards/*.jsx (StatCard, ServiceCard, DeploymentTargetCard...), codebase's
// Card.jsx, and several rds_ui *Card.jsx files each re-wrote independently.
// Two flavors are supported:
//   - Tailwind modules (golive/rds_ui): just use the default className.
//   - CSS-class modules (codebase): pass title/subtitle/right the way
//     codebase's old Card.jsx did — this component renders the same
//     card-header/card-body structure that codebase.css already styles,
//     so codebase can keep its own CSS untouched and only swap the import.
//
// Usage:
//   <SurfaceCard title="Deployments" right={<Button/>}>...</SurfaceCard>
//   <SurfaceCard padding="p-5" className="flex flex-col gap-3">...</SurfaceCard>

import PropTypes from "prop-types";

export default function SurfaceCard({
  title,
  subtitle,
  right,
  children,
  className = "",
  padding = "p-5",
  variant = "tailwind",
}) {
  if (variant === "legacy-css") {
    // Matches codebase.css's .card / .card-header / .card-body rules exactly,
    // so this is a drop-in replacement for the old codebase Card.jsx.
    return (
      <div className={`card ${className}`.trim()}>
        {(title || right) && (
          <div className="card-header">
            <div className="card-head-left">
              {title ? <div className="card-title">{title}</div> : null}
              {subtitle ? <div className="card-subtitle">{subtitle}</div> : null}
            </div>
            {right ? <div className="card-head-right">{right}</div> : null}
          </div>
        )}
        <div className="card-body">{children}</div>
      </div>
    );
  }

  return (
    <div className={`bg-white border border-gray-200 rounded-xl ${padding} ${className}`.trim()}>
      {(title || subtitle || right) && (
        <div className="flex items-center justify-between gap-3 mb-3">
          <div>
            {title && <p className="text-sm font-semibold text-slate-900">{title}</p>}
            {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
          </div>
          {right}
        </div>
      )}
      {children}
    </div>
  );
}

SurfaceCard.propTypes = {
  title: PropTypes.node,
  subtitle: PropTypes.node,
  right: PropTypes.node,
  children: PropTypes.node,
  className: PropTypes.string,
  padding: PropTypes.string,
  variant: PropTypes.oneOf(["tailwind", "legacy-css"]),
};
